//@flow
import * as Immutable from 'immutable';

import OutletActions from 'common/redux/actions/outlet';

import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { MasterLocation } from 'common/models/internal/MasterLocation';

import type { ActionType } from 'common/redux/reducers/baseEntityReducer';
import createBaseEntityReducer, {
    isRequestBodyCurrent
} from 'common/redux/reducers/baseEntityReducer';

const baseReducer = createBaseEntityReducer(OutletActions, MasterOutlet);

export const outlet = (
    state = Immutable.fromJS({ records: {} }),
    action: ActionType
) => {
    //Override with switch cases if we need to override
    switch (action.type) {
        case OutletActions.types.FETCH_ENTITIES_SUCCESS: {
            const { entities, params } = action;
            if (!entities) {
                throw new Error('expected action to have entities');
            }
            const { clientId } = params;
            if (!isRequestBodyCurrent({ state, action })) {
                // reject if prevRequestBody is not same as current body
                return state;
            }
            let newState = state.setIn(
                ['records', clientId, 'entities', 'status', 'isLoading'],
                false
            );

            entities.entities = entities.outlets.map(entity =>
                MasterOutlet.fromJS(entity)
            );
            delete entities.outlets;
            newState = newState.setIn(
                ['records', clientId, 'entities', 'records'],
                entities
            );
            newState = newState.deleteIn([
                'records',
                clientId,
                'entities',
                'error'
            ]);
            return newState;
        }

        case OutletActions.types.ADD_ENTITY_SUCCESS: {
            const { entity, params } = action;
            if (!entity) {
                throw new Error('expected action to have entity');
            }
            const { clientId } = params;

            const updatedOutlet = MasterOutlet.fromJS(entity);

            const entities = state.getIn([
                'records',
                clientId,
                'entities',
                'records'
            ]);
            let newState = state;
            if (entities) {
                const updatedOutlets = entities.entities.map(outlet => {
                    if (
                        outlet.clientId === updatedOutlet.clientId &&
                        outlet.id === updatedOutlet.id
                    ) {
                        return updatedOutlet;
                    }
                    return outlet;
                });

                newState = newState.setIn(
                    ['records', clientId, 'entities', 'records'],
                    Object.assign({}, entities, { entities: updatedOutlets })
                );
            }

            return newState;
        }

        case OutletActions.types.OUTLET_BULK_GEOCODE_SUCCESS: {
            const { params, locations, response } = action;
            const { clientId } = params;
            const existingOutlets =
                state.getIn(['records', clientId, 'entities', 'records']) || {};
            // get outlet id to locationId info
            const outletIdToLocationIdMap = locations.reduce(
                (acc, locationEntity) => {
                    locationEntity.metadata.forEach(outletId => {
                        acc[outletId] = locationEntity.id;
                    });
                    return acc;
                },
                {}
            );

            // update outlets
            let newState = state;
            if (existingOutlets.entities) {
                const updatedOutlets = existingOutlets.entities.map(
                    outletEntity => {
                        const { id: outletId } = outletEntity;
                        if (outletIdToLocationIdMap[outletId]) {
                            const updatedLocation = response.find(
                                responseEntity =>
                                    responseEntity.id ===
                                    outletIdToLocationIdMap[outletId]
                            );
                            if (updatedLocation) {
                                return outletEntity.setLocationInfo(
                                    outletEntity.locationInfo.setLocation(
                                        MasterLocation.fromJS(updatedLocation)
                                    )
                                );
                            }
                            return outletEntity;
                        }
                        return outletEntity;
                    }
                );
                newState = newState.setIn(
                    ['records', clientId, 'entities', 'records'],
                    { ...existingOutlets, entities: updatedOutlets }
                );
            }
            return newState;
        }

        case OutletActions.types.FETCH_ALL_OUTLET_SUCCESS: {
            const {
                params: { clientId },
                entities
            } = action;

            let newState = state.setIn(
                ['records', clientId, 'entities', 'status', 'isLoading'],
                false
            );

            const _entities = {
                existingOutlets: [],
                allOutlets: [],
                entities: [],
                paginationInfo: {}
            };

            _entities.allOutlets = entities.map(entity =>
                MasterOutlet.fromJS(entity)
            );

            const pagedOutlets = _entities.allOutlets.splice(0, 15);
            _entities.entities = pagedOutlets;
            _entities.paginationInfo = {
                currentPage: 1,
                numberOfPages: Math.ceil(_entities.allOutlets.length / 15),
                total: _entities.allOutlets.length
            };

            newState = newState.setIn(
                ['records', clientId, 'entities', 'records'],
                _entities
            );

            return newState;
        }

        case OutletActions.types.FETCH_EXISTING_OUTLET_SUCCESS: {
            const {
                params: { clientId },
                entities,
                body
            } = action;

            let newState = state.setIn(
                ['records', clientId, 'entities', 'status', 'isLoading'],
                false
            );

            const _entities = state.getIn(
                ['records', clientId, 'entities', 'records'],
                {}
            );

            _entities.existingOutlets = entities.outlets.map(entity =>
                MasterOutlet.fromJS(entity)
            );
            _entities.entities = _entities.existingOutlets.splice(0, 14);
            _entities.paginationInfo = {
                ...entities.paginationInfo,
                currentPage: 1,
                numberOfPages: Math.ceil(
                    _entities.existingOutlets.length / body.size
                )
            };

            newState = newState.setIn(
                ['records', clientId, 'entities', 'records'],
                _entities
            );

            return newState;
        }
    }

    return baseReducer(state, action);
};
